import React, { useState, useEffect } from 'react';
import Footer from '../common/Footer';
import Sidebar from '../Sidebar';
import studentService from '../../services/studentService';
import admissionService from '../../services/admissionService';
import AuthImage from '../common/AuthImage';

/* ─── tiny helpers ──────────────────────────────── */
const statusLabel = (raw) => {
  if (!raw) return 'Pending';
  const s = raw.toString().toLowerCase().replace(/[_\s-]+/g, '_');
  const map = {
    pending: 'Pending', submitted: 'Pending', waiting: 'Waiting',
    approved: 'Confirmed', confirmed: 'Confirmed', rejected: 'Rejected',
    on_hold: 'On Hold', draft: 'Draft', allocated: 'Allocated',
    admitted: 'Admitted', active: 'Active',
  };
  return map[s] || (raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase());
};
const statusClass = (raw) => {
  if (!raw) return 'pending';
  const s = raw.toString().toLowerCase().replace(/[_\s-]+/g, '_');
  const map = {
    pending: 'pending', submitted: 'pending', waiting: 'waiting',
    approved: 'approved', confirmed: 'confirmed', rejected: 'rejected',
    on_hold: 'on_hold', draft: 'draft', allocated: 'allocated',
    admitted: 'admitted', active: 'active',
  };
  return map[s] || 'unknown';
};
const StatusBadge = ({ status }) => (
  <span className={`status-badge status-${statusClass(status)}`}>
    {statusLabel(status)}
  </span>
);

const formatDOB = (val) => {
  if (!val) return null;
  try {
    const d = new Date(val);
    if (isNaN(d)) return val;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
  } catch { return val; }
};

const Field = ({ label, value }) => (
  <div style={{ display:'flex', padding:'13px 0', borderBottom:'1px solid #F0EDE8', gap:12 }}>
    <div style={{ width:200, fontSize:11, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:'#6B6B6B', flexShrink:0, paddingTop:2 }}>{label}</div>
    <div style={{ fontSize:14, color:'#2C2C2C' }}>{value || <span style={{color:'#C4BEB8'}}>—</span>}</div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h4 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:'#1B3B5F', margin:'32px 0 4px', paddingBottom:10, borderBottom:'2px solid #1B3B5F' }}>{children}</h4>
);

/* ─── Update Status Modal ───────────────────────── */
const Toast = ({ message, onHide }) => {
  React.useEffect(() => { const t = setTimeout(onHide, 5000); return () => clearTimeout(t); }, [onHide]);
  return (
    <div style={{
      position:'fixed', top:24, right:24, zIndex:99999,
      background:'#2D5F4F', color:'#fff', padding:'14px 22px', borderRadius:8,
      boxShadow:'0 8px 32px rgba(0,0,0,0.22)', display:'flex', alignItems:'center',
      gap:12, fontSize:14, fontWeight:600, minWidth:300,
      animation:'toastIn 0.3s ease',
    }}>
      <span style={{ fontSize:18 }}>✓</span>
      <span>{message}</span>
      <button onClick={onHide} style={{ marginLeft:'auto', background:'none', border:'none', color:'#fff', fontSize:18, cursor:'pointer', opacity:0.7 }}>×</button>
      <style>{'@keyframes toastIn{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}'}</style>
    </div>
  );
};

const UpdateStatusModal = ({ currentStatus, onClose, onSubmit }) => {
  const [status, setStatus] = useState(currentStatus || 'PENDING');
  const [saving, setSaving] = useState(false);
  const options = [
    { value:'PENDING',  label:'Pending' },
    { value:'REJECTED', label:'Rejected' },
  ];
  const save = async () => { setSaving(true); await onSubmit(status); setSaving(false); };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(27,59,95,0.6)', zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', width:460, borderRadius:4, overflow:'hidden', boxShadow:'0 24px 64px rgba(0,0,0,0.3)' }}>
        <div style={{ background:'#1B3B5F', padding:'20px 30px' }}>
          <h3 style={{ color:'#fff', margin:0, fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:400 }}>Update Status</h3>
        </div>
        <div style={{ padding:30 }}>
          <div style={{ borderBottom:'2px solid #D4AF37', marginBottom:20 }}/>
          <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:'#6B6B6B', marginBottom:10 }}>Select Status</label>
          <select value={status} onChange={e=>setStatus(e.target.value)}
            style={{ width:'100%', padding:'14px 16px', border:'1px solid #E8E4DF', borderRadius:4, fontSize:14, fontFamily:'Work Sans,sans-serif', color:'#2C2C2C', outline:'none', background:'#FAFAF9', cursor:'pointer' }}>
            {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{ display:'flex', gap:12, marginTop:28, justifyContent:'flex-end' }}>
            <button onClick={onClose} style={{ padding:'11px 26px', border:'2px solid #E8E4DF', background:'transparent', borderRadius:4, cursor:'pointer', fontSize:13, fontWeight:600, color:'#6B6B6B' }}>Cancel</button>
            <button onClick={save} disabled={saving} style={{ padding:'11px 26px', background:'#D4AF37', color:'#fff', border:'none', borderRadius:4, cursor:saving?'not-allowed':'pointer', fontSize:13, fontWeight:600, opacity:saving?0.7:1 }}>
              {saving?'Saving…':'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ConfirmFG defined OUTSIDE modal so it never re-mounts on state change ────
const ConfirmFG = ({ label, k, children, lbl, err }) => (
  <div style={{ marginBottom:20 }}>
    <label style={lbl}>{label}</label>
    {children}
    {err(k)}
  </div>
);

/* ─── Confirm Admission Modal ───────────────────── */
const ConfirmAdmissionModal = ({ student, onClose, onConfirmed }) => {

  // ── Fee structure (from fee sheet) ────────────────────────────────────────
  const FOOD_PER_MONTH = 6000;
  const MAINTENANCE    = 5000;
  const DEPOSIT_FIXED  = 10000;
  const PREPAID_FIXED  = 15000;
  const MATTRESS_COST  = 5000;
  const ACCOM_DEFAULTS = { '4sharing':10500, '3sharing':12500, '2sharing':14500, '2sharingPremium':19000, 'private':29000 };

  const [rates, setRates] = useState({ rate4:0, rate3:0, rate2:0, rate2p:0, ratep:0 });
  const [form, setForm] = useState({
    roomType: '',
    dateOfAdmission: new Date().toISOString().split('T')[0],
    validTill: '',
    paymentMode: '',
    transactionId: '',
    feePayments: [], // [{key, label, amount, selected}]
    accommodation:true, breakfast:true, lunch:true, dinner:true,
    wifi:true, readingRoom:true, gymnasium:true,
    inst1:'', inst2:'', inst3:'', inst4:'', remarks:'',
  });
  const [errors,   setErrors]   = useState({});
  const [saving,   setSaving]   = useState(false);
  const [apiError, setApiError] = useState('');

  // Load room rates on mount
  useEffect(() => {
    const load = async () => {
      try {
        const api = (await import('../../services/api')).default;
        const res = await api.get('/room-rates');
        const d = res.data;
        setRates({ rate4:d.rate4||0, rate3:d.rate3||0, rate2:d.rate2||0, rate2p:d.rate2p||0, ratep:d.ratep||0 });
        // Auto-detect room type from student's allocated bed
        if (student?.admission?.bedCode) {
          try {
            const br = await api.get('/rooms/beds/' + student.admission.bedCode + '/room-type');
            if (br.data?.roomType) setForm(f => ({ ...f, roomType: br.data.roomType }));
          } catch(e) { /* not allocated yet — admin selects manually */ }
        }
      } catch(e) {
        setRates({ rate4:10500, rate3:12500, rate2:14500, rate2p:19000, ratep:29000 });
      }
    };
    load();
  }, []);

  const setF = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: '' })); };

  // ── Auto-calculations ─────────────────────────────────────────────────────
  const getAccomRate = (rt) => {
    if (rt === '4sharing')        return rates.rate4  || ACCOM_DEFAULTS['4sharing'];
    if (rt === '3sharing')        return rates.rate3  || ACCOM_DEFAULTS['3sharing'];
    if (rt === '2sharing')        return rates.rate2  || ACCOM_DEFAULTS['2sharing'];
    if (rt === '2sharingPremium') return rates.rate2p || ACCOM_DEFAULTS['2sharingPremium'];
    if (rt === 'private')         return rates.ratep  || ACCOM_DEFAULTS['private'];
    return 0;
  };
  const countMonths = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start), e = new Date(end);
    if (e <= s) return 0;
    return Math.max(1, (e.getFullYear()-s.getFullYear())*12+(e.getMonth()-s.getMonth())+1);
  };

  const accomRate = getAccomRate(form.roomType);
  const months    = countMonths(form.dateOfAdmission, form.validTill);
  const accomAmt  = accomRate * months;
  const foodAmt   = FOOD_PER_MONTH * months;
  const maintAmt  = form.roomType ? MAINTENANCE : 0;
  const deposit   = form.roomType ? DEPOSIT_FIXED : 0;
  const card      = form.roomType ? PREPAID_FIXED : 0;
  const totalFees = accomAmt + foodAmt + maintAmt + deposit + card;
  const fmtINR    = (n) => n ? '₹ ' + Number(n).toLocaleString('en-IN') : '—';

  const validate = () => {
    const e = {};
    if (!form.roomType)        e.roomType = 'Please select a room type';
    if (!form.dateOfAdmission) e.dateOfAdmission = 'Required';
    if (!form.validTill)       e.validTill = 'Required';
    if (!form.paymentMode)     e.paymentMode = 'Required';
    const selectedPayments = (form.feePayments || []).filter(p => p.selected && parseFloat(p.amount) > 0);
    if (selectedPayments.length === 0) e.amountPaid = 'Select at least one fee head and enter amount';
    if (form.paymentMode && form.paymentMode !== 'CASH' && !form.transactionId.trim()) e.transactionId = 'Required for online payment';
    // Installment dates are optional
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const selectedPayments = (form.feePayments||[]).filter(p=>p.selected && parseFloat(p.amount||0)>0);
      await admissionService.allocate({
        studentId: student.id, wardenName:'', wardenContact:'',
        hostelFees: accomAmt, foodCharges: foodAmt, maintenanceCharges: maintAmt,
        gst: 0, refundableDeposit: deposit, prepaidCard: card, mattressCharge: 0,
        totalFees: totalFees, roomType: form.roomType, months: months,
        dateOfAdmission: form.dateOfAdmission, validTill: form.validTill,
        paymentMode: form.paymentMode, transactionId: form.transactionId || null,
        paidFees: selectedPayments.reduce((s,p)=>s+parseFloat(p.amount||0),0),
        feePayments: selectedPayments.map(p=>({ feeHead:p.key, feeHeadLabel:p.label, amount:parseFloat(p.amount||0) })),
        remarks: selectedPayments.map(p=>`[${p.label}]`).join(', ') || form.remarks || null,
        accommodation:form.accommodation, breakfast:form.breakfast, lunch:form.lunch,
        dinner:form.dinner, wifi:form.wifi, readingRoom:form.readingRoom,
        gymnasium:form.gymnasium, transport:false,
        inst1:form.inst1||null, inst2:form.inst2||null,
        inst3:form.inst3||null, inst4:form.inst4||null,
      });
      // Print separate receipt for each fee head
      if (selectedPayments.length > 0) {
        const now = new Date();
        const dateStr = now.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
        const receiptWindow = window.open('','_blank','width=900,height=1200');
        if (receiptWindow) {
          receiptWindow.document.write(`<!DOCTYPE html><html><head><title>Receipts</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=Cormorant+Garamond:wght@300;400;600&display=swap');
            *{box-sizing:border-box;margin:0;padding:0;}
            body{font-family:'DM Sans',sans-serif;background:#f5f5f5;padding:20px;}
            .receipt{width:794px;min-height:1123px;margin:0 auto 40px;background:#fff;padding:60px;border:1px solid #ddd;page-break-after:always;position:relative;}
            .top-header{text-align:center;border-bottom:3px solid #1B3B5F;padding-bottom:24px;margin-bottom:30px;}
            .company-name{font-family:'Cormorant Garamond',serif;font-size:32px;font-weight:600;color:#1B3B5F;letter-spacing:2px;}
            .company-sub{font-size:12px;color:#6B6B6B;margin-top:4px;letter-spacing:1px;}
            .receipt-title{display:inline-block;margin-top:16px;background:#1B3B5F;color:#fff;padding:8px 32px;font-size:13px;font-weight:700;letter-spacing:3px;text-transform:uppercase;}
            .meta-row{display:flex;justify-content:space-between;margin-bottom:30px;}
            .meta-box{font-size:13px;color:#6B6B6B;line-height:1.8;}
            .meta-box strong{color:#1B3B5F;font-weight:700;}
            .divider{border:none;border-top:1px solid #e8e4df;margin:20px 0;}
            .table{width:100%;border-collapse:collapse;margin-bottom:30px;}
            .table th{background:#1B3B5F;color:#fff;padding:12px 16px;text-align:left;font-size:12px;letter-spacing:1px;text-transform:uppercase;font-weight:700;}
            .table td{padding:14px 16px;border-bottom:1px solid #f0ede8;font-size:14px;color:#2C2C2C;}
            .table tr:last-child td{border-bottom:none;}
            .amount-box{background:#1B3B5F;color:#fff;padding:20px 30px;display:flex;justify-content:space-between;align-items:center;margin-bottom:30px;border-radius:4px;}
            .amount-label{font-size:13px;letter-spacing:1px;text-transform:uppercase;opacity:0.8;}
            .amount-value{font-family:'Cormorant Garamond',serif;font-size:36px;font-weight:600;color:#D4AF37;}
            .amount-words{font-size:12px;color:#6B6B6B;margin-top:6px;font-style:italic;}
            .footer{position:absolute;bottom:60px;left:60px;right:60px;}
            .sign-row{display:flex;justify-content:space-between;align-items:flex-end;padding-top:40px;border-top:1px solid #e8e4df;}
            .sign-block{text-align:center;}
            .sign-line{width:160px;border-top:1px solid #2C2C2C;margin-bottom:6px;}
            .sign-label{font-size:11px;color:#6B6B6B;font-weight:700;letter-spacing:1px;text-transform:uppercase;}
            .watermark{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);font-size:80px;color:rgba(27,59,95,0.04);font-family:'Cormorant Garamond',serif;font-weight:700;pointer-events:none;white-space:nowrap;}
            @media print{body{background:#fff;padding:0;}.receipt{margin:0;border:none;box-shadow:none;min-height:auto;}button{display:none;}}
          </style></head><body>`);
          selectedPayments.forEach((p, idx) => {
            const rNo = `OHR-${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}-${String(idx+1).padStart(4,'0')}`;
            const isFood = p.key === 'food' || p.label?.toLowerCase().includes('food');
            const companyName = isFood ? 'Moonlight Trading Corporation' : 'Omkara Impex and Merchandise LLP';
            const companySub  = isFood ? 'Food Services Provider' : 'Hostel Management Services';
            const amtNum = Number(p.amount);
            receiptWindow.document.write(`
            <div class="receipt">
              <div class="watermark">RECEIPT</div>
              <div class="top-header">
                <div class="company-name">${companyName}</div>
                <div class="company-sub">${companySub}</div>
                <div class="receipt-title">Payment Receipt</div>
              </div>
              <div class="meta-row">
                <div class="meta-box">
                  <div><strong>Receipt No.:</strong> ${rNo}</div>
                  <div><strong>Date:</strong> ${dateStr}</div>
                  <div><strong>Payment Mode:</strong> ${form.paymentMode||'CASH'}</div>
                  ${form.transactionId ? `<div><strong>Transaction ID:</strong> ${form.transactionId}</div>` : ''}
                </div>
                <div class="meta-box" style="text-align:right;">
                  <div><strong>Student Name:</strong> ${student.fullName||student.name||''}</div>
                  <div><strong>Student ID:</strong> ${student.registrationId||student.admissionNumber||'—'}</div>
                  <div><strong>Mobile:</strong> ${student.mobile||'—'}</div>
                </div>
              </div>
              <hr class="divider"/>
              <table class="table">
                <thead>
                  <tr><th>Description</th><th>Fee Head</th><th style="text-align:right;">Amount (₹)</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Hostel Fee Payment — Academic Year 2026–27</td>
                    <td>${p.label}</td>
                    <td style="text-align:right;font-weight:700;">₹${amtNum.toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
              </table>
              <div class="amount-box">
                <div>
                  <div class="amount-label">Total Amount Paid</div>
                  <div class="amount-words">Rupees ${amtNum.toLocaleString('en-IN')} only</div>
                </div>
                <div class="amount-value">₹${amtNum.toLocaleString('en-IN')}</div>
              </div>
              <div class="footer">
                <div class="sign-row">
                  <div class="sign-block">
                    <div class="sign-line"></div>
                    <div class="sign-label">Student Signature</div>
                  </div>
                  <div class="sign-block" style="text-align:right;">
                    <div class="sign-line" style="margin-left:auto;"></div>
                    <div class="sign-label">Authorised Signatory</div>
                    <div style="font-size:11px;color:#1B3B5F;margin-top:4px;font-weight:700;">${companyName}</div>
                  </div>
                </div>
              </div>
            </div>`);
          });
          receiptWindow.document.write('</body></html>');
          receiptWindow.document.close();
          setTimeout(() => receiptWindow.print(), 600);
        }
      }
      onConfirmed();
    } catch (error) {
      const status  = error.response?.status;
      const message = error.response?.data?.message;
      if (status === 409) { setApiError(message || 'Admission already confirmed for this student.'); }
      else if (error.response?.data?.errors) { setErrors(error.response.data.errors); }
      else { setApiError(message || 'Failed to confirm admission. Please try again.'); }
    } finally { setSaving(false); }
  };

  // ── Style helpers — defined inside but only primitive values, no re-mount issue
  const inp   = (hasErr) => ({ width:'100%', padding:'13px 0', border:'none', borderBottom:`1px solid ${hasErr?'#E85D4A':'#E8E4DF'}`, background:'transparent', fontSize:14, outline:'none', fontFamily:'Work Sans,sans-serif', color:'#2C2C2C' });
  const roInp = { width:'100%', padding:'13px 0', border:'none', borderBottom:'1px solid #E8E4DF', background:'transparent', fontSize:14, outline:'none', fontFamily:'Work Sans,sans-serif', color:'#1B3B5F', fontWeight:600, cursor:'not-allowed' };
  const lbl   = { display:'block', fontSize:11, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:'#6B6B6B', marginBottom:6 };
  const err   = (k) => errors[k] ? <p style={{color:'#E85D4A',fontSize:11,marginTop:3}}>{errors[k]}</p> : null;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(27,59,95,0.65)', zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', width:760, maxHeight:'94vh', overflowY:'auto', borderRadius:4, boxShadow:'0 24px 64px rgba(0,0,0,0.35)' }}>

        {/* Header */}
        <div style={{ background:'#1B3B5F', padding:'22px 40px', position:'sticky', top:0, zIndex:10 }}>
          <h3 style={{ color:'#fff', margin:0, fontFamily:'Cormorant Garamond,serif', fontSize:26, fontWeight:400 }}>Confirm Admission</h3>
          <p style={{ color:'rgba(255,255,255,0.7)', margin:'4px 0 0', fontSize:13 }}>{student?.fullName} — {student?.registrationId}</p>
        </div>

        <div style={{ padding:40 }}>

          {/* Room Type */}
          <div style={{ background:'#F4F1ED', borderLeft:'3px solid #1B3B5F', padding:'14px 18px', borderRadius:'0 4px 4px 0', marginBottom: errors.roomType ? 8 : 28 }}>
            <label style={{...lbl, marginBottom:8}}>Room Type *</label>
            <select value={form.roomType} onChange={e=>setF('roomType',e.target.value)}
              style={{ width:'100%', padding:'11px 0', border:'none', borderBottom:'1px solid #C8C3BC', background:'transparent', fontSize:15, fontWeight:600, color:'#1B3B5F', outline:'none', fontFamily:'Work Sans,sans-serif', cursor:'pointer', appearance:'auto' }}>
              <option value="">— Select Room Type —</option>
              <option value="4sharing">4 Sharing</option>
              <option value="3sharing">3 Sharing</option>
              <option value="2sharing">2 Sharing (Twin)</option>
              <option value="2sharingPremium">2 Sharing (Premium)</option>
              <option value="private">Single / Private</option>
            </select>
          </div>
          {err('roomType')}

          {/* Rate strip */}
          {form.roomType && (
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:24 }}>
              {[
                { label:'Accommodation / mo', val: fmtINR(accomRate) },
                { label:'Food / mo',          val: fmtINR(FOOD_PER_MONTH) },
                { label:'Total / mo',         val: fmtINR(accomRate + FOOD_PER_MONTH) },
              ].map(ch => (
                <div key={ch.label} style={{ padding:'5px 12px', background:'#EEF2F7', borderRadius:20, fontSize:12, color:'#1B3B5F', fontWeight:600 }}>
                  <span style={{ color:'#6B6B6B', fontWeight:400 }}>{ch.label}: </span>{ch.val}
                </div>
              ))}
            </div>
          )}

          {/* Main grid */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 40px', marginBottom:8 }}>

            {/* Left — Fee Details */}
            <div>
              <h4 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, color:'#1B3B5F', marginBottom:16 }}>Fee Details</h4>

              <ConfirmFG label="Date of Admission *" k="dateOfAdmission" lbl={lbl} err={err}>
                <input type="date" style={inp(errors.dateOfAdmission)} value={form.dateOfAdmission} onChange={e=>setF('dateOfAdmission',e.target.value)} />
              </ConfirmFG>

              <ConfirmFG label="Valid Till *" k="validTill" lbl={lbl} err={err}>
                <input type="date" style={inp(errors.validTill)} value={form.validTill} onChange={e=>setF('validTill',e.target.value)} />
              </ConfirmFG>

              {/* Auto-filled read-only fields */}
              <div style={{ marginBottom:20 }}>
                <label style={lbl}>No. of Months (Billed)</label>
                <input readOnly style={roInp} value={months ? months+(months===1?' month':' months') : ''} placeholder="—" />
                <p style={{fontSize:11,color:'#9B8FA0',marginTop:3,fontStyle:'italic'}}>Admission month always billed in full</p>
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={lbl}>Accommodation Charges (₹)</label>
                <input readOnly style={roInp} value={accomAmt ? fmtINR(accomAmt) : ''} placeholder="—" />
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={lbl}>Food Charges — Breakfast, Lunch &amp; Dinner (₹)</label>
                <input readOnly style={roInp} value={foodAmt ? fmtINR(foodAmt) : ''} placeholder="—" />
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={lbl}>Maintenance Fix Charges (₹)</label>
                <input readOnly style={roInp} value={maintAmt ? fmtINR(maintAmt) : ''} placeholder="—" />
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={lbl}>Interest Free Refundable Security Deposit (₹)</label>
                <input readOnly style={roInp} value={deposit ? fmtINR(deposit) : ''} placeholder="—" />
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={lbl}>Prepaid Card — Laundry, Electricity, Cafeteria (₹)</label>
                <input readOnly style={roInp} value={card ? fmtINR(card) : ''} placeholder="—" />
              </div>


              {/* Total block */}
              <div style={{ background:'#F4F1ED', borderRadius:4, padding:'18px 20px', marginBottom:24 }}>
                {[
                  { label:'Accommodation Charges',   val: accomAmt },
                  { label:'Food Charges',            val: foodAmt },
                  { label:'Maintenance Fix Charges', val: maintAmt },
                  { label:'Security Deposit',        val: deposit },
                  { label:'Prepaid Card',            val: card },
                ].map((row, i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderTop:i===0?'none':'1px solid #E8E4DF' }}>
                    <span style={{ fontSize:12, color:'#6B6B6B' }}>{row.label}</span>
                    <span style={{ fontSize:13, fontWeight:600, color:'#2C2C2C', fontFamily:'monospace' }}>{fmtINR(row.val)}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0 6px', borderTop:'2px solid #C8C3BC', marginTop:4 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:'#1B3B5F' }}>Total Payable (₹)</span>
                  <span style={{ fontSize:20, fontWeight:700, color:'#1B3B5F', fontFamily:'monospace' }}>{totalFees ? fmtINR(totalFees) : '₹ —'}</span>
                </div>
                <p style={{ fontSize:10, color:'#9B8FA0', textAlign:'right', marginTop:4, fontStyle:'italic' }}>GST @ 0% — not currently charged</p>
              </div>

              <ConfirmFG label="Payment Mode *" k="paymentMode" lbl={lbl} err={err}>
                <select style={{...inp(errors.paymentMode), cursor:'pointer'}} value={form.paymentMode} onChange={e=>setF('paymentMode',e.target.value)}>
                  <option value="">Select</option>
                  {['CASH','ONLINE','CARD','CHEQUE','DD'].map(v=><option key={v} value={v}>{v}</option>)}
                </select>
              </ConfirmFG>

              {form.paymentMode && form.paymentMode !== 'CASH' && (
                <ConfirmFG label="Transaction ID *" k="transactionId" lbl={lbl} err={err}>
                  <input style={inp(errors.transactionId)} placeholder="Enter transaction reference" value={form.transactionId} onChange={e=>setF('transactionId',e.target.value)} />
                </ConfirmFG>
              )}

              {/* Multi-select Fee Heads with auto-filled amounts */}
              {(() => {
                const isSuperOwner = true; // ✅ All roles can edit fee amounts
                const FEE_HEAD_LIST = [
                  { key:'accommodation', label:'Accommodation Charges', icon:'🏠', defaultAmt: accomAmt },
                  { key:'food',          label:'Food Charges',          icon:'🍽️', defaultAmt: foodAmt  },
                  { key:'maintenance',   label:'Maintenance Charges (Fix per Annum)', icon:'🔧', defaultAmt: maintAmt },
                  { key:'deposit',       label:'Refundable Deposit',    icon:'🔒', defaultAmt: deposit  },
                  { key:'prepaidCard',   label:'Prepaid Card Load',     icon:'💳', defaultAmt: card     },
                  { key:'other',         label:'Other',                 icon:'📋', defaultAmt: 0, disabled:true },
                ];
                // Always sync defaultAmt when recalculated
                // Always use live-calculated defaultAmt so amounts reflect room type + months
                const payments = FEE_HEAD_LIST.map(h => {
                  const existing = (form.feePayments||[]).find(p => p.key === h.key);
                  return {
                    ...h,
                    selected: existing ? existing.selected : false,
                    // Non-superowner: always use live defaultAmt (recalculated from room type/months)
                    // Superowner: use their edited amount if exists
                    amount: isSuperOwner && existing?.amount !== undefined ? existing.amount : h.defaultAmt,
                  };
                });

                const toggleHead = (key) => {
                  const head = payments.find(p => p.key === key);
                  if (!head || head.disabled) return; // ✅ skip if disabled
                  const updated = payments.map(p =>
                    p.key === key ? { ...p, selected: !p.selected } : p
                  );
                  setF('feePayments', updated);
                };
                const setHeadAmt = (key, val) => {
                  if (!isSuperOwner) return; // only superowner can edit
                  setF('feePayments', payments.map(p => p.key === key ? { ...p, amount: val } : p));
                };
                const selectedTotal = payments.filter(p=>p.selected).reduce((s,p)=>s+parseFloat(p.amount||0),0);

                return (
                  <div style={{ marginBottom:20 }}>
                    <label style={{ display:'block', fontSize:11, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:'#6B6B6B', marginBottom:6 }}>
                      Payment For * <span style={{ color:'#9B9B9B', fontWeight:400, textTransform:'none', letterSpacing:0 }}>(select one or more)</span>
                    </label>

                    <div style={{ border:'1.5px solid #E8E4DF', borderRadius:10, overflow:'hidden' }}>
                      {payments.map((h, i) => (
                        <div key={h.key} style={{
                          display:'flex', alignItems:'center', gap:10, padding:'11px 14px',
                          borderBottom: i < payments.length-1 ? '1px solid #F0EDE8' : 'none',
                          background: h.disabled ? '#F8F6F3' : h.selected ? '#F0F6FF' : '#fff',
                          transition:'background 0.15s',
                          opacity: h.disabled ? 0.5 : 1,
                          cursor: h.disabled ? 'not-allowed' : 'default',
                        }}>
                          {/* Checkbox */}
                          <div onClick={() => !h.disabled && toggleHead(h.key)} style={{
                            width:20, height:20, borderRadius:5, flexShrink:0,
                            cursor: h.disabled ? 'not-allowed' : 'pointer',
                            border:`2px solid ${h.disabled ? '#E8E4DF' : h.selected ? '#1B3B5F' : '#C4BEB8'}`,
                            background: h.disabled ? '#E8E4DF' : h.selected ? '#1B3B5F' : '#fff',
                            display:'flex', alignItems:'center', justifyContent:'center',
                          }}>
                            {h.selected && !h.disabled && <span style={{ color:'#fff', fontSize:11, fontWeight:800 }}>✓</span>}
                          </div>
                          <span style={{ fontSize:17, flexShrink:0 }}>{h.icon}</span>
                          <span onClick={() => !h.disabled && toggleHead(h.key)}
                            style={{ fontSize:13, fontWeight:600, flex:1,
                              cursor: h.disabled ? 'not-allowed' : 'pointer',
                              color: h.disabled ? '#C4BEB8' : h.selected ? '#1B3B5F' : '#6B6B6B' }}>
                            {h.label}
                            {h.disabled && <span style={{ fontSize:10, fontWeight:400, marginLeft:6, color:'#C4BEB8' }}>(not available)</span>}
                          </span>
                          {/* Amount — always shown, auto-filled, editable only for superowner */}
                          <div style={{ display:'flex', alignItems:'center', gap:5, flexShrink:0 }}>
                            <span style={{ fontSize:12, color:'#6B6B6B' }}>₹</span>
                            <input
                              type="number"
                              value={h.disabled ? '' : (isSuperOwner ? (payments.find(p=>p.key===h.key)?.amount ?? h.defaultAmt) : (h.defaultAmt || ''))}
                              onChange={e => !h.disabled && setHeadAmt(h.key, e.target.value)}
                              readOnly={h.disabled || !isSuperOwner}
                              disabled={h.disabled}
                              placeholder={h.disabled ? '—' : (h.defaultAmt > 0 ? '' : '0')}
                              style={{
                                width:100, padding:'5px 8px',
                                border: h.disabled ? '1px solid #E8E4DF' : h.selected
                                  ? `2px solid ${isSuperOwner ? '#D4AF37' : '#1B3B5F'}`
                                  : '1px solid #E8E4DF',
                                borderRadius:6, fontSize:12, fontWeight:700,
                                fontFamily:'monospace', textAlign:'right',
                                color: h.disabled ? '#C4BEB8' : h.selected ? '#1B3B5F' : '#9B9B9B',
                                background: h.disabled ? '#F0EDE8' : !isSuperOwner ? '#F8F6F3' : '#fff',
                                cursor: h.disabled ? 'not-allowed' : isSuperOwner ? 'text' : 'default',
                                outline:'none', boxSizing:'border-box',
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {payments.some(p=>p.selected) && (
                      <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 14px',
                        background:'#1B3B5F', borderRadius:'0 0 10px 10px', marginTop:-1 }}>
                        <span style={{ fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.8)' }}>Total Paying Now</span>
                        <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:700, color:'#D4AF37' }}>
                          ₹{selectedTotal.toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                    {errors.amountPaid && <div style={{ color:'#E85D4A', fontSize:12, marginTop:6 }}>{errors.amountPaid}</div>}
                  </div>
                );
              })()}
            </div>

            {/* Right — Amenities */}
            <div>
              <h4 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, color:'#1B3B5F', marginBottom:16 }}>Amenities Included</h4>
              {[['accommodation','Accommodation'],['breakfast','Breakfast'],['lunch','Lunch'],['dinner','Dinner'],['wifi','Wi-Fi'],['readingRoom','Reading Room'],['gymnasium','Gymnasium']].map(([k,label])=>(
                <label key={k} style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16, cursor:'pointer' }}>
                  <input type="checkbox" checked={form[k]} onChange={e=>setF(k,e.target.checked)}
                    style={{ width:16, height:16, accentColor:'#1B3B5F', cursor:'pointer' }} />
                  <span style={{ fontSize:14, color:'#2C2C2C' }}>{label}</span>
                </label>
              ))}

              {/* Additional */}
              <div style={{ background:'#FAFAF8', border:'1px solid #E8E4DF', borderRadius:4, padding:16, marginTop:8 }}>
              </div>

              {/* Installments */}
              <div style={{ marginTop:28 }}>
                <h4 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, color:'#1B3B5F', marginBottom:16 }}>
                  Installment Dates
                  <span style={{ fontSize:12, fontWeight:400, color:'#6B6B6B', marginLeft:8 }}>(optional)</span>
                </h4>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'0 20px' }}>
                  {[1,2,3,4].map(n=>(
                    <ConfirmFG key={n} label={`Instalment ${n}`} k={`inst${n}`} lbl={lbl} err={err}>
                      <input type="date" style={inp(false)} value={form[`inst${n}`]}
                        onChange={e=>setF(`inst${n}`,e.target.value)} />
                    </ConfirmFG>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Remarks */}
          <ConfirmFG label="Remarks" k="remarks" lbl={lbl} err={err}>
            <textarea style={{...inp(), resize:'none', height:64}} placeholder="Optional notes…" value={form.remarks} onChange={e=>setF('remarks',e.target.value)} />
          </ConfirmFG>

          {apiError && (
            <div style={{ margin:'0 0 16px', padding:'14px 18px', background:'#FFF3F3', border:'1px solid #E85D4A', borderLeft:'4px solid #E85D4A', borderRadius:4, color:'#E85D4A', fontSize:14, fontWeight:500, display:'flex', alignItems:'center', gap:10 }}>
              <span style={{fontSize:18}}>⚠️</span><span>{apiError}</span>
            </div>
          )}

          {/* Actions */}
          <div style={{ display:'flex', gap:14, justifyContent:'flex-end', marginTop:12, paddingTop:24, borderTop:'1px solid #E8E4DF' }}>
            <button onClick={onClose} style={{ padding:'13px 28px', border:'2px solid #E8E4DF', background:'transparent', borderRadius:4, cursor:'pointer', fontSize:13, fontWeight:600, color:'#6B6B6B' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={saving} style={{ padding:'13px 32px', background:'#1B3B5F', color:'#fff', border:'none', borderRadius:4, cursor:saving?'not-allowed':'pointer', fontSize:13, fontWeight:600, opacity:saving?0.7:1 }}>
              {saving ? 'Confirming…' : 'Confirm Admission'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Edit Registration Screen ──────────────────── */
const CAP_FIELDS = new Set([
  'fullName','fatherName','motherName','localGuardianName','guardianRelation',
  'religion','nativeRegion',
  'permAddress1','permAddress2','permCity','permState','permCountry',
  'corrAddress1','corrAddress2','corrCity','corrState','corrCountry',
]);
const cap = v => v && v.length > 0 ? v.charAt(0).toUpperCase() + v.slice(1) : v;

const editInp = (hasErr) => ({
  width:'100%', padding:'13px 0', border:'none',
  borderBottom:`1px solid ${hasErr?'#E85D4A':'#E8E4DF'}`,
  background:'transparent', fontSize:14, outline:'none',
  fontFamily:'Work Sans,sans-serif', color:'#2C2C2C',
});
const editLbl = { display:'block', fontSize:11, fontWeight:700, letterSpacing:1, textTransform:'uppercase', color:'#6B6B6B', marginBottom:6 };
const EditFG = ({ label, k, form, errors, setF, children }) => (
  <div style={{marginBottom:22}}>
    <label style={editLbl}>{label}</label>
    {children || <input style={editInp(!!errors[k])} value={form[k]||''} onChange={e=>setF(k,e.target.value)} />}
    {errors[k] && <p style={{color:'#E85D4A',fontSize:11,marginTop:3}}>{errors[k]}</p>}
  </div>
);

const EditRegistration = ({ student, onBack, onSaved }) => {
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [removePhotoFlag, setRemovePhotoFlag] = useState(false);
  const [docFiles, setDocFiles] = useState({});
  const setDocFile = (key, file) => setDocFiles(p => ({ ...p, [key]: file }));

  useEffect(() => {
    if (!student) return;
    if (student.photoUrl) {
      studentService.fetchImageAsBlob(studentService.getPhotoUrl(student.id, true))
        .then(blob => setPhotoPreview(blob || null));
    } else { setPhotoPreview(null); }
    setForm({
      fullName:student.fullName||'', dob:student.dob||'', gender:student.gender||'',
      maritalStatus:student.maritalStatus||'', religion:student.religion||'', bloodGroup:student.bloodGroup||'',
      nativeRegion:student.nativeRegion||'', mobile:student.mobile||'', email:student.email||'',
      fatherName:student.fatherName||'', fatherMobile:student.fatherMobile||'', fatherEmail:student.fatherEmail||'',
      motherName:student.motherName||'', motherMobile:student.motherMobile||'', motherEmail:student.motherEmail||'',
      permAddress1:student.permanentAddress?.address1||'', permAddress2:student.permanentAddress?.address2||'',
      permCity:student.permanentAddress?.city||'', permZipcode:student.permanentAddress?.zipcode||'',
      permState:student.permanentAddress?.state||'', permCountry:student.permanentAddress?.country||'India',
      corrAddress1:student.correspondenceAddress?.address1||'', corrAddress2:student.correspondenceAddress?.address2||'',
      corrCity:student.correspondenceAddress?.city||'', corrZipcode:student.correspondenceAddress?.zipcode||'',
      corrState:student.correspondenceAddress?.state||'', corrCountry:student.correspondenceAddress?.country||'India',
      localGuardianName:student.localGuardianName||'', guardianRelation:student.guardianRelation||'',
      guardianMobile:student.guardianMobile||'', guardianEmail:student.guardianEmail||'',
      guardianIdType:student.guardianIdType||'', guardianIdNumber:student.guardianIdNumber||'',
      aadhar:student.aadhar||'', emergencyContact:student.emergencyContact||'',
      drivingLicense:student.drivingLicense||'', passport:student.passport||'',
      // ✅ Pre-fill courses and language from existing student data
      courses: student.courses && student.courses.length > 0
        ? student.courses.map(c=>({
            universityName: c.universityName||'',
            academicYear:   c.academicYear||'',
            stream:         c.stream||'',
            timing:         c.timing||'',
            phoneNo:        c.phoneNo||'',
          }))
        : [{ universityName:'', academicYear:'', stream:'', timing:'', phoneNo:'' }],
      languagesToWrite: student.languagesToWrite||'',
      languagesToSpeak: student.languagesToSpeak||'',
      languagesToRead:  student.languagesToRead||'',
      motherTongue:     student.motherTongue||'',
    });
  }, [student]);

  const setF = (k,v) => { const val = CAP_FIELDS.has(k) ? cap(v) : v; setForm(p=>({...p,[k]:val})); setErrors(p=>({...p,[k]:''})); };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Select image file only'); return; }
    if (file.size > 2 * 1024 * 1024) { alert('Max size 2MB'); return; }
    setPhotoFile(file); setRemovePhotoFlag(false);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };
  const removePhoto = () => { setPhotoPreview(null); setPhotoFile(null); setRemovePhotoFlag(true); };

  const validate = () => {
    const e = {};
    if (!form.fullName?.trim()) e.fullName = 'Full name is required';
    if (!form.dob) e.dob = 'Date of birth is required';
    if (!form.gender) e.gender = 'Gender is required';
    if (!form.mobile?.trim()) e.mobile = 'Mobile is required';
    if (!form.email?.trim()) e.email = 'Email is required';
    if (!form.aadhar?.trim()) e.aadhar = 'Aadhar is required';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload = {
        fullName:form.fullName, dob:form.dob, gender:form.gender,
        maritalStatus:form.maritalStatus, religion:form.religion, bloodGroup:form.bloodGroup,
        nativeRegion:form.nativeRegion, mobile:form.mobile, email:form.email,
        fatherName:form.fatherName, fatherMobile:form.fatherMobile||'', fatherEmail:form.fatherEmail||'',
        motherName:form.motherName||'', motherMobile:form.motherMobile||'', motherEmail:form.motherEmail||'',
        permanentAddress:{ address1:form.permAddress1, address2:form.permAddress2||'', city:form.permCity, zipcode:form.permZipcode, state:form.permState, country:form.permCountry },
        correspondenceAddress:{ address1:form.corrAddress1||form.permAddress1, address2:form.corrAddress2||'', city:form.corrCity||form.permCity, zipcode:form.corrZipcode||form.permZipcode, state:form.corrState||form.permState, country:form.corrCountry||form.permCountry },
        localGuardianName:form.localGuardianName||'', guardianRelation:form.guardianRelation||'',
        guardianMobile:form.guardianMobile||'', guardianEmail:form.guardianEmail||'',
        guardianIdType:form.guardianIdType||'', guardianIdNumber:form.guardianIdNumber||'',
        aadhar:form.aadhar, emergencyContact:form.emergencyContact||'',
        drivingLicense:form.drivingLicense||'', passport:form.passport||'',
        termsAccepted:true, photoUrl:student.photoUrl,
        courses: (form.courses||[]).filter(c=>c.universityName.trim()).map(c=>({
          universityName: c.universityName,
          academicYear:   c.academicYear,
          stream:         c.stream,
          timing:         c.timing,
          phoneNo:        c.phoneNo,
        })),
        languagesToWrite:  form.languagesToWrite||'',
        languagesToSpeak:  form.languagesToSpeak||'',
        languagesToRead:   form.languagesToRead||'',
        motherTongue:      form.motherTongue||'',
      };
      await studentService.updateStudent(student.id, payload);
      if (photoFile) { await studentService.uploadPhoto(student.id, photoFile); }
      if (Object.keys(docFiles).length > 0) { await studentService.uploadIdProofs(student.id, docFiles); }
      if (removePhotoFlag) { await studentService.deletePhoto(student.id); setPhotoPreview(null); }
      onSaved();
    } catch (err) { alert(err.message || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const Sel = React.useCallback(({k, children}) => (
    <select style={{width:'100%', padding:'13px 0', border:'none', borderBottom:`1px solid ${errors[k]?'#E85D4A':'#E8E4DF'}`, background:'transparent', fontSize:14, outline:'none', fontFamily:'Work Sans,sans-serif', color:'#2C2C2C', cursor:'pointer'}} value={form[k]||''} onChange={e=>setF(k,e.target.value)}>{children}</select>
  ), [form, errors, setF]);

  return (
    <div className="app-layout">
      <Sidebar activeItem="registration" onNavigate={onBack} onLogout={()=>{}} />
      <main className="main-content">
        <div className="top-bar">
          <h1 className="page-title">Edit Registration</h1>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <span style={{ fontSize:13, color:'#6B6B6B' }}>Ref. ID: <strong style={{color:'#1B3B5F'}}>{student?.registrationId}</strong></span>
          </div>
        </div>
        <div className="content-area">
          <div className="wizard-card" style={{marginBottom:0}}>
            {/* Photo */}
            <div style={{ display:'flex', alignItems:'center', gap:20, marginBottom:30 }}>
              <div style={{ width:120, height:120, borderRadius:'50%', border:'1px solid #E8E4DF', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:40, background:'#F8F6F3' }}>
                {photoPreview ? <img src={photoPreview} alt="Profile" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : '👤'}
              </div>
              <div>
                <input type="file" accept="image/*" onChange={handlePhotoUpload} />
                {photoPreview && <div style={{ marginTop:8 }}><button type="button" onClick={removePhoto} style={{ color:'#E85D4A', background:'none', border:'none', cursor:'pointer' }}>Remove Photo</button></div>}
              </div>
            </div>

            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',marginBottom:28}}>👤 Personal Details</h4>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 40px'}}>
              <EditFG form={form} errors={errors} setF={setF} label="Full Name *" k="fullName" />
              <EditFG form={form} errors={errors} setF={setF} label="Date of Birth *" k="dob"><input type="date" style={editInp(!!errors['dob'])} value={form.dob||''} onChange={e=>setF('dob',e.target.value)} /></EditFG>
              <EditFG form={form} errors={errors} setF={setF} label="Gender *" k="gender"><Sel k="gender"><option value="">Select</option><option>Male</option><option>Female</option><option>Other</option></Sel></EditFG>
              <EditFG form={form} errors={errors} setF={setF} label="Marital Status" k="maritalStatus"><Sel k="maritalStatus"><option value="">Select</option><option>Single</option><option>Married</option></Sel></EditFG>
              <EditFG form={form} errors={errors} setF={setF} label="Religion" k="religion" />
              <EditFG form={form} errors={errors} setF={setF} label="Blood Group" k="bloodGroup"><Sel k="bloodGroup"><option value="">Select</option>{['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(b=><option key={b}>{b}</option>)}</Sel></EditFG>
              <EditFG form={form} errors={errors} setF={setF} label="Native Region" k="nativeRegion" />
              <EditFG form={form} errors={errors} setF={setF} label="Mobile No. *" k="mobile" />
              <EditFG form={form} errors={errors} setF={setF} label="Email *" k="email"><input type="email" style={editInp(!!errors['email'])} value={form.email||''} onChange={e=>setF('email',e.target.value)} /></EditFG>
            </div>

            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:'36px 0 28px',paddingTop:28,borderTop:'1px solid #E8E4DF'}}>👪 Family Details</h4>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 40px'}}>
              <EditFG form={form} errors={errors} setF={setF} label="Father's Name" k="fatherName" />
              <EditFG form={form} errors={errors} setF={setF} label="Father's Mobile" k="fatherMobile" />
              <EditFG form={form} errors={errors} setF={setF} label="Father's Email" k="fatherEmail"><input type="email" style={editInp(!!errors['fatherEmail'])} value={form.fatherEmail||''} onChange={e=>setF('fatherEmail',e.target.value)} /></EditFG>
              <EditFG form={form} errors={errors} setF={setF} label="Mother's Name" k="motherName" />
              <EditFG form={form} errors={errors} setF={setF} label="Mother's Mobile" k="motherMobile" />
              <EditFG form={form} errors={errors} setF={setF} label="Mother's Email" k="motherEmail"><input type="email" style={editInp(!!errors['motherEmail'])} value={form.motherEmail||''} onChange={e=>setF('motherEmail',e.target.value)} /></EditFG>
            </div>

            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:'36px 0 28px',paddingTop:28,borderTop:'1px solid #E8E4DF'}}>🏠 Permanent Address</h4>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 40px'}}>
              <EditFG form={form} errors={errors} setF={setF} label="Address Line 1" k="permAddress1" />
              <EditFG form={form} errors={errors} setF={setF} label="Address Line 2" k="permAddress2" />
              <EditFG form={form} errors={errors} setF={setF} label="City" k="permCity" />
              <EditFG form={form} errors={errors} setF={setF} label="Zip Code" k="permZipcode" />
              <EditFG form={form} errors={errors} setF={setF} label="State" k="permState" />
              <EditFG form={form} errors={errors} setF={setF} label="Country" k="permCountry" />
            </div>
            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:'32px 0 28px'}}>Correspondence Address</h4>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 40px'}}>
              <EditFG form={form} errors={errors} setF={setF} label="Address Line 1" k="corrAddress1" />
              <EditFG form={form} errors={errors} setF={setF} label="Address Line 2" k="corrAddress2" />
              <EditFG form={form} errors={errors} setF={setF} label="City" k="corrCity" />
              <EditFG form={form} errors={errors} setF={setF} label="Zip Code" k="corrZipcode" />
              <EditFG form={form} errors={errors} setF={setF} label="State" k="corrState" />
              <EditFG form={form} errors={errors} setF={setF} label="Country" k="corrCountry" />
            </div>

            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:'36px 0 28px',paddingTop:28,borderTop:'1px solid #E8E4DF'}}>🧳 Local Guardian</h4>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 40px'}}>
              <EditFG form={form} errors={errors} setF={setF} label="Guardian Name" k="localGuardianName" />
              <EditFG form={form} errors={errors} setF={setF} label="Relation" k="guardianRelation" />
              <EditFG form={form} errors={errors} setF={setF} label="Guardian Mobile" k="guardianMobile" />
              <EditFG form={form} errors={errors} setF={setF} label="Guardian Email" k="guardianEmail"><input type="email" style={editInp(!!errors['guardianEmail'])} value={form.guardianEmail||''} onChange={e=>setF('guardianEmail',e.target.value)} /></EditFG>
              <EditFG form={form} errors={errors} setF={setF} label="Guardian ID Type" k="guardianIdType">
                <select style={editInp(false)} value={form.guardianIdType||''} onChange={e=>{setF('guardianIdType',e.target.value);setF('guardianIdNumber','');}}>
                  <option value="">Select ID type...</option>
                  <option value="Aadhar">Aadhar Card</option>
                  <option value="DrivingLicense">Driving License</option>
                  <option value="Passport">Passport</option>
                </select>
              </EditFG>
              {form.guardianIdType && (
                <EditFG form={form} errors={errors} setF={setF} label="Guardian ID Number" k="guardianIdNumber">
                  <input style={editInp(!!errors['guardianIdNumber'])} value={form.guardianIdNumber||''}
                    placeholder={form.guardianIdType==='Aadhar'?'12-digit number':form.guardianIdType==='Passport'?'e.g. A1234567':'e.g. MH1220150012345'}
                    onChange={e=>setF('guardianIdNumber', form.guardianIdType==='Aadhar' ? e.target.value.replace(/\D/g,'').slice(0,12) : e.target.value.toUpperCase())}
                    maxLength={form.guardianIdType==='Aadhar'?12:form.guardianIdType==='Passport'?8:20}
                  />
                </EditFG>
              )}
            </div>
            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:'32px 0 28px',paddingTop:28,borderTop:'1px solid #E8E4DF'}}>🪪 ID Proof Numbers</h4>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 40px',marginBottom:32}}>
              <EditFG form={form} errors={errors} setF={setF} label="Aadhar Card No. *" k="aadhar" />
              <EditFG form={form} errors={errors} setF={setF} label="Emergency Contact" k="emergencyContact" />
              <EditFG form={form} errors={errors} setF={setF} label="Driving Licence No." k="drivingLicense" />
              <EditFG form={form} errors={errors} setF={setF} label="Passport No." k="passport" />
              <EditFG form={form} errors={errors} setF={setF} label="PAN Card No." k="panNumber" />
            </div>

            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:'0 0 8px'}}>📎 ID Proof Documents</h4>
            <p style={{fontSize:12,color:'#6B6B6B',marginBottom:20,marginTop:0}}>Upload or replace front and back sides. Only selected files will be updated.</p>
            {[
              { label:'Aadhar Card',     fk:'aadharFront',        bk:'aadharBack',          curF:student.aadharFrontUrl,        curB:student.aadharBackUrl },
              { label:'Driving Licence', fk:'drivingLicenseFront', bk:'drivingLicenseBack',  curF:student.drivingLicenseFrontUrl, curB:student.drivingLicenseBackUrl },
              { label:'Passport',        fk:'passportFront',       bk:'passportBack',         curF:student.passportFrontUrl,       curB:student.passportBackUrl },
              { label:'PAN Card',        fk:'panFront',            bk:'panBack',              curF:student.panFrontUrl,            curB:student.panBackUrl },
            ].map(doc => (
              <div key={doc.label} style={{marginBottom:18, padding:16, border:'1px solid #E8E4DF', borderRadius:8, background:'#FAFAF9'}}>
                <div style={{fontSize:13, fontWeight:700, color:'#1B3B5F', marginBottom:12}}>{doc.label}</div>
                <div style={{display:'flex', gap:16}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#6B6B6B',textTransform:'uppercase',letterSpacing:.8,marginBottom:6}}>⬆ Front Side</div>
                    {doc.curF && !docFiles[doc.fk] && <AuthImage url={studentService.getDocumentUrl(student.id, doc.curF)} alt="front" style={{width:'100%',maxHeight:90,objectFit:'cover',borderRadius:4,border:'1px solid #E8E4DF',marginBottom:6}} fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:60,background:"#F4F1ED",borderRadius:6,fontSize:12,color:"#6B6B6B"}}>📄 View</div>}/>}
                    {docFiles[doc.fk] && <div style={{fontSize:11,color:'#22a06b',fontWeight:600,marginBottom:6}}>✓ {docFiles[doc.fk].name}</div>}
                    <label style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',background:'#1B3B5F',color:'#fff',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:600}}>
                      📎 {doc.curF ? 'Replace' : 'Upload'}<input type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={e=>e.target.files[0]&&setDocFile(doc.fk,e.target.files[0])}/>
                    </label>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#6B6B6B',textTransform:'uppercase',letterSpacing:.8,marginBottom:6}}>⬇ Back Side</div>
                    {doc.curB && !docFiles[doc.bk] && <AuthImage url={studentService.getDocumentUrl(student.id, doc.curB)} alt="back" style={{width:'100%',maxHeight:90,objectFit:'cover',borderRadius:4,border:'1px solid #E8E4DF',marginBottom:6}} fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:60,background:"#F4F1ED",borderRadius:6,fontSize:12,color:"#6B6B6B"}}>📄 View</div>}/>}
                    {docFiles[doc.bk] && <div style={{fontSize:11,color:'#22a06b',fontWeight:600,marginBottom:6}}>✓ {docFiles[doc.bk].name}</div>}
                    <label style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',background:'#1B3B5F',color:'#fff',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:600}}>
                      📎 {doc.curB ? 'Replace' : 'Upload'}<input type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={e=>e.target.files[0]&&setDocFile(doc.bk,e.target.files[0])}/>
                    </label>
                  </div>
                </div>
              </div>
            ))}
            <div style={{marginBottom:24, padding:16, border:'1px solid #E8E4DF', borderRadius:8, background:'#FAFAF9'}}>
              <div style={{fontSize:13, fontWeight:700, color:'#1B3B5F', marginBottom:12}}>Light Bill (Residential Proof)</div>
              {student.lightBillUrl && !docFiles.lightBill && <AuthImage url={studentService.getDocumentUrl(student.id, student.lightBillUrl)} alt="light bill" style={{width:'50%',maxHeight:90,objectFit:'cover',borderRadius:4,border:'1px solid #E8E4DF',marginBottom:6}} fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:60,background:"#F4F1ED",borderRadius:6,fontSize:12,color:"#6B6B6B"}}>📄 View</div>}/>}
              {docFiles.lightBill && <div style={{fontSize:11,color:'#22a06b',fontWeight:600,marginBottom:6}}>✓ {docFiles.lightBill.name}</div>}
              <label style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',background:'#1B3B5F',color:'#fff',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:600}}>
                📎 {student.lightBillUrl ? 'Replace Light Bill' : 'Upload Light Bill'}<input type="file" accept="image/*,.pdf" style={{display:'none'}} onChange={e=>e.target.files[0]&&setDocFile('lightBill',e.target.files[0])}/>
              </label>
            </div>

            {/* ✅ Course & Education Details — same structure as View UI */}
            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:'36px 0 28px',paddingTop:28,borderTop:'1px solid #E8E4DF'}}>🎓 Course and Education Details</h4>
            {(form.courses||[]).map((course, idx) => (
              <div key={idx} style={{marginBottom:24}}>
                {/* Course header with remove button */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#6B6B6B',textTransform:'uppercase',letterSpacing:1}}>University {idx+1}</div>
                  {(form.courses||[]).length > 1 && (
                    <button type="button"
                      onClick={() => setForm(p=>({...p, courses:p.courses.filter((_,i)=>i!==idx)}))}
                      style={{background:'none',border:'none',color:'#E85D4A',cursor:'pointer',fontSize:12,fontWeight:600}}>
                      ✕ Remove
                    </button>
                  )}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 40px'}}>
                  <EditFG form={{['universityName_'+idx]:course.universityName}} errors={errors} setF={(_,v)=>setForm(p=>({...p,courses:p.courses.map((c,i)=>i===idx?{...c,universityName:v}:c)}))} label="University Name *" k={'universityName_'+idx} />
                  <EditFG form={{['academicYear_'+idx]:course.academicYear}} errors={errors} setF={(_,v)=>setForm(p=>({...p,courses:p.courses.map((c,i)=>i===idx?{...c,academicYear:v}:c)}))} label="Academic Year *" k={'academicYear_'+idx}>
                    <input style={editInp(false)} value={course.academicYear||''} placeholder="e.g. 2026-27"
                      onChange={e=>setForm(p=>({...p,courses:p.courses.map((c,i)=>i===idx?{...c,academicYear:e.target.value}:c)}))} />
                  </EditFG>
                  <EditFG form={{['stream_'+idx]:course.stream}} errors={errors} setF={(_,v)=>setForm(p=>({...p,courses:p.courses.map((c,i)=>i===idx?{...c,stream:v}:c)}))} label="Stream *" k={'stream_'+idx} />
                  <EditFG form={{['timing_'+idx]:course.timing}} errors={errors} setF={(_,v)=>setForm(p=>({...p,courses:p.courses.map((c,i)=>i===idx?{...c,timing:v}:c)}))} label="Timing *" k={'timing_'+idx} />
                  <EditFG form={{['phoneNo_'+idx]:course.phoneNo}} errors={errors} setF={(_,v)=>setForm(p=>({...p,courses:p.courses.map((c,i)=>i===idx?{...c,phoneNo:v}:c)}))} label="Phone No. *" k={'phoneNo_'+idx} />
                </div>
                {idx < (form.courses||[]).length-1 && <div style={{borderTop:'1px solid #E8E4DF',marginTop:16}} />}
              </div>
            ))}
            <button type="button"
              onClick={()=>setForm(p=>({...p,courses:[...(p.courses||[]),{universityName:'',academicYear:'',stream:'',timing:'',phoneNo:''}]}))}
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 18px',background:'transparent',border:'1.5px solid #1B3B5F',color:'#1B3B5F',borderRadius:6,cursor:'pointer',fontSize:12,fontWeight:600,marginBottom:8}}>
              + Add Another Course
            </button>

            {/* ✅ Language Details — same structure as View UI */}
            <h4 style={{fontFamily:'Cormorant Garamond,serif',fontSize:22,color:'#1B3B5F',margin:'36px 0 28px',paddingTop:28,borderTop:'1px solid #E8E4DF'}}>🗣 Language Details</h4>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 40px'}}>
              <EditFG form={form} errors={errors} setF={setF} label="To Write" k="languagesToWrite" />
              <EditFG form={form} errors={errors} setF={setF} label="To Speak" k="languagesToSpeak" />
              <EditFG form={form} errors={errors} setF={setF} label="To Read"  k="languagesToRead" />
              <EditFG form={form} errors={errors} setF={setF} label="Mother Tongue" k="motherTongue" />
            </div>

            <div style={{display:'flex',justifyContent:'flex-end',gap:12,marginTop:40,paddingTop:24,borderTop:'1px solid #E8E4DF'}}>
              <button className="btn-back" onClick={()=>onBack(student)}>Cancel</button>
              <button className="btn-submit" style={{ opacity:saving?0.7:1, padding:'10px 38px', width:'auto' }} onClick={handleSave} disabled={saving}>{saving?'Saving…':'Update'}</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

/* ─── MAIN VIEW ──────────────────────────────────── */
const statusColor = { PENDING:'#4A7BA7', APPROVED:'#27ae60', REJECTED:'#E85D4A', DRAFT:'#6B6B6B' };

const RegistrationView = ({ onNavigate, onLogout, registrationId }) => {
  const [student,       setStudent]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [showStatus,    setShowStatus]    = useState(false);
  const [showAdmission, setShowAdmission] = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [savedToast,    setSavedToast]    = useState(false);

  useEffect(() => { load(); }, [registrationId]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      let data;
      if (registrationId) { data = await studentService.getStudentByRegistrationId(registrationId); }
      else {
        const saved = JSON.parse(localStorage.getItem('completeRegistrationData')||'null');
        if (saved?.id) data = await studentService.getStudentById(saved.id);
        else throw new Error('No registration data found');
      }
      setStudent(data);
    } catch(e) { setError(e.message||'Failed to load registration'); }
    finally { setLoading(false); }
  };

  const handleUpdateStatus = async (newStatus) => {
    try { await studentService.updateStudentStatus(student.id, newStatus); setStudent(s=>({...s,status:newStatus})); setShowStatus(false); }
    catch(e) { alert(e.message||'Failed to update status'); }
  };
  const handleAdmissionConfirmed = () => { setShowAdmission(false); onNavigate('admission'); };

  if (editMode && student) {
    return <EditRegistration student={student} onBack={(s) => { setEditMode(false); if(s) load(); }} onSaved={() => { setSavedToast(true); setEditMode(false); load(); }} />;
  }

  const BtnGold = ({ children, onClick }) => (
    <button onClick={onClick} style={{ padding:'11px 26px', background:'#D4AF37', color:'#fff', border:'none', borderRadius:4, cursor:'pointer', fontWeight:700, fontSize:13, letterSpacing:0.5, transition:'all 0.2s', fontFamily:'Work Sans,sans-serif' }}
      onMouseEnter={e=>e.target.style.background='#C19A2E'} onMouseLeave={e=>e.target.style.background='#D4AF37'}
    >{children}</button>
  );

  const addr = (a) => a ? `${[a.address1,a.address2,a.city,a.zipcode,a.state,a.country].filter(Boolean).join(', ')}` : '—';

  return (
    <div className="app-layout">
      {savedToast && <Toast message="Student details updated successfully." onHide={() => setSavedToast(false)} />}
      <Sidebar activeItem="registration" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content">

        <div style={{ background:'#FFFFFF', padding:'28px 36px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #E6E1D9' }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:32, color:'#1B3B5F', fontWeight:500, letterSpacing:.3 }}>
            Application Review - {student?.fullName}
          </div>
          <button onClick={()=>onNavigate('dashboard')} style={{ padding:'11px 26px', background:'transparent', border:'2px solid #1B3B5F', borderRadius:4, fontWeight:600, letterSpacing:.5, cursor:'pointer' }}>
            ← BACK TO REGISTRATIONS
          </button>
        </div>

        <div className="content-area">
          <div style={{ background:'#FFFFFF', padding:'28px 36px', display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'30px', borderBottom:'1px solid #E6E1D9' }}>
            <div>
              <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:32, color:'#1B3B5F', fontWeight:500, letterSpacing:.3 }}>Application Review</div>
              <div style={{ marginTop:10, fontSize:13, color:'#6B6B6B' }}>Reference ID: {student?.registrationId}</div>
            </div>
          </div>

          {loading ? (
            <div style={{padding:60,textAlign:'center',color:'#6B6B6B',fontSize:15}}>Loading registration details…</div>
          ) : error ? (
            <div style={{padding:40,background:'#FFF3F3',borderLeft:'4px solid #E85D4A',color:'#E85D4A',margin:20,borderRadius:4}}>{error}</div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:24}}>
              <div className="wizard-card" style={{padding:40}}>
                <SectionTitle>Personal Details</SectionTitle>
                <Field label="Full Name" value={student.fullName} />
                <Field label="Date of Birth" value={formatDOB(student.dob)} />
                <Field label="Gender" value={student.gender} />
                <Field label="Marital Status" value={student.maritalStatus} />
                <Field label="Religion" value={student.religion} />
                <Field label="Blood Group" value={student.bloodGroup} />
                <Field label="Mobile No." value={student.mobile} />
                <Field label="Native Region" value={student.nativeRegion} />
                <Field label="Email ID" value={student.email} />

                <SectionTitle>Family Details</SectionTitle>
                <Field label="Father's Name" value={student.fatherName} />
                <Field label="Father's Mobile" value={student.fatherMobile} />
                <Field label="Father's Email" value={student.fatherEmail} />
                <Field label="Mother's Name" value={student.motherName} />
                <Field label="Mother's Mobile" value={student.motherMobile} />
                <Field label="Mother's Email" value={student.motherEmail} />

                <SectionTitle>Permanent Address</SectionTitle>
                <Field label="Address" value={addr(student.permanentAddress)} />
                <SectionTitle>Correspondence Address</SectionTitle>
                <Field label="Address" value={addr(student.correspondenceAddress)} />

                <SectionTitle>Local Guardian</SectionTitle>
                <Field label="Guardian Name" value={student.localGuardianName} />
                <Field label="Relation" value={student.guardianRelation} />
                <Field label="Guardian Mobile" value={student.guardianMobile} />
                <Field label="Guardian Email" value={student.guardianEmail} />
                {student.guardianIdType && <Field label="Guardian ID Type" value={student.guardianIdType==='DrivingLicense'?'Driving License':student.guardianIdType==='Passport'?'Passport':student.guardianIdType==='Aadhar'?'Aadhar Card':student.guardianIdType} />}
                {student.guardianIdNumber && <Field label="Guardian ID Number" value={student.guardianIdNumber} />}

                <SectionTitle>ID Proof</SectionTitle>
                <Field label="Aadhar Card No." value={student.aadhar} />
                <Field label="Emergency Contact" value={student.emergencyContact} />
                <Field label="Driving Licence" value={student.drivingLicense} />
                <Field label="Passport No." value={student.passport} />
                <Field label="PAN Card No." value={student.panNumber} />

                {[
                  { label:'Aadhar Card',     frontUrl:student.aadharFrontUrl,         backUrl:student.aadharBackUrl },
                  { label:'Driving Licence', frontUrl:student.drivingLicenseFrontUrl,  backUrl:student.drivingLicenseBackUrl },
                  { label:'Passport',        frontUrl:student.passportFrontUrl,        backUrl:student.passportBackUrl },
                  { label:'PAN Card',        frontUrl:student.panFrontUrl,             backUrl:student.panBackUrl },
                ].filter(d => d.frontUrl || d.backUrl).map(doc => (
                  <div key={doc.label} style={{ marginBottom:20 }}>
                    <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#1B3B5F', padding:'10px 0 6px', borderTop:'1px solid #E8E4DF' }}>{doc.label}</div>
                    <div style={{ display:'flex', gap:16, marginTop:8 }}>
                      {doc.frontUrl && <div style={{flex:1}}><div style={{fontSize:11,color:'#6B6B6B',fontWeight:600,marginBottom:6}}>⬆ FRONT</div><a href={studentService.getDocumentUrl(student.id,doc.frontUrl)} target="_blank" rel="noreferrer"><AuthImage url={studentService.getDocumentUrl(student.id,doc.frontUrl)} alt={doc.label+' front'} style={{width:'100%',maxHeight:160,objectFit:'cover',borderRadius:6,border:'1px solid #E8E4DF',cursor:'pointer'}} fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:60,background:"#F4F1ED",borderRadius:6,fontSize:12,color:"#6B6B6B"}}>📄 View</div>}/></a></div>}
                      {doc.backUrl && <div style={{flex:1}}><div style={{fontSize:11,color:'#6B6B6B',fontWeight:600,marginBottom:6}}>⬇ BACK</div><a href={studentService.getDocumentUrl(student.id,doc.backUrl)} target="_blank" rel="noreferrer"><AuthImage url={studentService.getDocumentUrl(student.id,doc.backUrl)} alt={doc.label+' back'} style={{width:'100%',maxHeight:160,objectFit:'cover',borderRadius:6,border:'1px solid #E8E4DF',cursor:'pointer'}} fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:60,background:"#F4F1ED",borderRadius:6,fontSize:12,color:"#6B6B6B"}}>📄 View</div>}/></a></div>}
                    </div>
                  </div>
                ))}
                {student.lightBillUrl && (
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'#1B3B5F', padding:'10px 0 6px', borderTop:'1px solid #E8E4DF' }}>Light Bill</div>
                    <div style={{ marginTop:8, width:'50%' }}>
                      <a href={studentService.getDocumentUrl(student.id,student.lightBillUrl)} target="_blank" rel="noreferrer">
                        <AuthImage url={studentService.getDocumentUrl(student.id,student.lightBillUrl)} alt="Light Bill" style={{width:'100%',maxHeight:160,objectFit:'cover',borderRadius:6,border:'1px solid #E8E4DF',cursor:'pointer'}} fallback={<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:60,background:"#F4F1ED",borderRadius:6,fontSize:12,color:"#6B6B6B"}}>📄 View Light Bill</div>}/>
                      </a>
                    </div>
                  </div>
                )}

                {student.courses?.length > 0 && (<>
                  <SectionTitle>Course and Education Details</SectionTitle>
                  {student.courses.map((c,i)=>(<div key={i}>
                    <Field label={`University ${i+1}`} value={c.universityName} />
                    <Field label="Academic Year" value={c.academicYear} />
                    <Field label="Stream" value={c.stream} />
                    <Field label="Timing" value={c.timing} />
                    <Field label="Phone No." value={c.phoneNo} />
                  </div>))}
                </>)}

                <SectionTitle>Language Details</SectionTitle>
                <Field label="To Write" value={student.languagesToWrite} />
                <Field label="To Speak" value={student.languagesToSpeak} />
                <Field label="To Read" value={student.languagesToRead} />
                <Field label="Mother Tongue" value={student.motherTongue} />
              </div>

              {/* Sidebar */}
              <div>
                <div style={{ background:'#FFFFFF', border:'1px solid #E6E1D9', borderRadius:2, padding:'40px 28px', textAlign:'center', position:'sticky', top:100, width:'100%', maxWidth:320, margin:'0 auto' }}>
                  <div style={{ width:120, height:120, borderRadius:'50%', border:'3px solid #C9A227', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 24px', fontSize:40, background:'#F4F1EC' }}>
                    {student.photoUrl ? (
                      <AuthImage url={studentService.getPhotoUrl(student.id)} alt="Student" style={{ width:120, height:120, objectFit:'cover', borderRadius:'50%' }} fallback={<span style={{fontSize:40}}>👤</span>} />
                    ) : <span style={{fontSize:40}}>👤</span>}
                  </div>
                  <div style={{ fontFamily:'Cormorant Garamond, serif', fontSize:28, color:'#1B3B5F', marginBottom:10 }}>{student.fullName}</div>
                  <div style={{ fontSize:12, color:'#6B6B6B', marginBottom:28 }}>Registration ID: {student.registrationId}</div>
                  <div style={{ borderTop:'1px solid #E6E1D9', marginBottom:26 }}/>
                  <div style={{fontSize:13,color:'#6B6B6B',marginBottom:12}}>Application Status</div>
                  <StatusBadge status={student.status} />
                </div>
              </div>
            </div>
          )}

          {/* Bottom Action Buttons */}
          <div style={{ display:'flex', justifyContent:'space-between', gap:30, padding:'26px 36px', background:'#FFFFFF', borderTop:'1px solid #E6E1D9', marginTop:0 }}>
            <button onClick={()=>onNavigate('dashboard')} style={{ flex:1, padding:'16px 0', background:'#FFFFFF', border:'2px solid #1B3B5F', color:'#1B3B5F', fontWeight:600, letterSpacing:1, cursor:'pointer' }}>← BACK TO LIST</button>
            <button onClick={()=>setShowStatus(true)} style={{ flex:1, padding:'16px 0', background:'#E2573E', border:'none', color:'#fff', fontWeight:700, letterSpacing:1, cursor:'pointer' }}>⚡ UPDATE STATUS</button>
            <button onClick={()=>setEditMode(true)} style={{ flex:1, padding:'16px 0', background:'#FFFFFF', border:'2px solid #1B3B5F', color:'#1B3B5F', fontWeight:600, letterSpacing:1, cursor:'pointer' }}>✎ EDIT APPLICATION</button>
            <button onClick={()=>setShowAdmission(true)} style={{ flex:1, padding:'16px 0', background:'#C9A227', border:'none', color:'#fff', fontWeight:700, letterSpacing:1, cursor:'pointer' }}>✓ CONFIRM ADMISSION</button>
          </div>
        </div>

        {showStatus && <UpdateStatusModal currentStatus={student?.status} onClose={()=>setShowStatus(false)} onSubmit={handleUpdateStatus} />}
        {showAdmission && student && <ConfirmAdmissionModal student={student} onClose={()=>setShowAdmission(false)} onConfirmed={handleAdmissionConfirmed} />}
        <Footer />
      </main>
    </div>
  );
};

export default RegistrationView;